import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { formatNumber } from '@/utils/format'
import { progressPercent } from '../status'

interface ProgressMeterProps {
  entered: number
  expected: number
  label: string
  className?: string
  /** Hide the "x / y" caption. */
  compact?: boolean
}

/** Mark-entry progress: marks entered out of marks expected. */
export function ProgressMeter({ entered, expected, label, className, compact }: ProgressMeterProps) {
  const { t } = useTranslation('exams')
  const pct = progressPercent(entered, expected)
  const done = expected > 0 && entered >= expected
  return (
    <div className={cn('min-w-0', className)}>
      {!compact ? (
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
          <span className="text-muted">{label}</span>
          <span className="font-medium text-ink-soft tabular">
            {formatNumber(entered)} / {formatNumber(expected)} · {pct}%
          </span>
        </div>
      ) : null}
      <div
        className="h-1.5 overflow-hidden rounded-full bg-surface-sunken"
        role="progressbar"
        aria-label={label}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={t('{{entered}} of {{expected}} marks entered', { entered, expected })}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-300',
            done ? 'bg-success-500' : 'bg-brand-600',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
